import { AxiosRequestConfig } from "axios";
import { pickBy } from "lodash-es";
import loglevel from "loglevel";
import { isDev } from "shared/utils/DevUtils";

const ignoredPaths = ["/frontend/ping", "/feature-flags/context/"];

const log = loglevel.getLogger("MissingRouteLoader");
log.setDefaultLevel("silent");

/**
 * Detects if a request was not preloaded due to a missing route loader.
 *
 * This function tries to detect if a route loader is missing by
 * looking at the stack trace to see where the query originated.
 *
 * Only runs in development mode.
 *
 * To resolve the warning caused by this function:
 *
 *   1. Find the request in the network tab of the browser dev tools.
 *   2. Select the request and look at the "Initiator" tab to see
 *      the request call stack. This will contain the name of the query
 *      hook (e.g. `useFundListRetrieve`) and the name of the component
 *      containing the hook (e.g. `FundListTable`).
 *   3. Search up the component tree to find the parent route component,
 *      e.g. `FundListPage`.
 *   4. Find the route definition and add the corresponding loader:
 *      ```ts
 *      {
 *        id: "fundList"
 *        path: "/funds",
 *        component: FundListPage,
 *        loader: getFundListRetrieveLoader(),
 *      }
 *      ```
 *      The name of the loader is the name of the query hook with
 *      "use" replaced by "get" and "Loader" appended to the end.
 *      See "orval-loader-generator.ts" for details.
 *
 * If this does not resolve the warning, the loader might need additional
 * parameters. Look inside the loader function for details.
 *
 * Note that the parameters must match exactly.
 * `{}` is not the same as `undefined` or `null`.
 *
 * @see https://reactrouter.com/en/main/route/loader
 *
 * @param {AxiosRequestConfig} config - The Axios request configuration.
 */
export const detectMissingRouteLoader = (config: AxiosRequestConfig) => {
  if (!isDev) {
    return;
  }
  const currentStackTrace = new Error().stack ?? "";
  const url = config.url ?? "";
  if (
    config.method?.toLowerCase() === "get" &&
    !ignoredPaths.includes(url) &&
    !currentStackTrace.includes("prefetchQuery") &&
    !currentStackTrace.includes("setOptions")
  ) {
    log.warn("missing route loader for:", url, pickBy(config.params));
  }
};
